import { useNotifications } from '../../hooks/useNotifications';
import NotificationItem from './NotificationItem';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";

interface NotificationPanelProps {
  onClose: () => void;
}

export default function NotificationPanel({
  onClose }: NotificationPanelProps) {
  const { t } = useTranslation();
  const { notifications, unreadCount, loading, markAllAsRead } = useNotifications();
  const navigate = useNavigate();

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleViewAll = () => {
    navigate('/notifications');
    onClose();
  };

  return (
    <div className="w-80 md:w-[440px] bg-white/95 dark:bg-gray-950/95 backdrop-blur-2xl border border-white/50 dark:border-gray-800 rounded-[32px] shadow-[0_30px_90px_-10px_rgba(0,0,0,0.2)] overflow-hidden">
      {/* Header Estilo Checklist UI2 */}
      <div className="px-7 py-6 border-b border-gray-100 dark:border-gray-800/50 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight leading-none">
              {t('common.notificaciones')}
            </h3>
            {unreadCount > 0 && (
              <span className="text-[10px] font-black text-[#AAD08C] uppercase tracking-[0.2em] mt-1.5 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#AAD08C] animate-pulse" />
                {unreadCount} {t('notifications.nuevas', { defaultValue: 'Nuevas' })}
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="text-[9px] font-black text-[#AAD08C] hover:text-[#AAD08C]/80 uppercase tracking-widest bg-[#AAD08C]/10 px-3.5 py-2 rounded-full transition-all active:scale-95 border border-[#AAD08C]/20"
            >
              {t('common.marcartodas')}
            </button>
          )}
        </div>
      </div>

      {/* Content con scroll suave, responsivo en altura para encajar en la pantalla */}
      <div 
        className="overflow-y-auto scrollbar-hide"
        style={{ maxHeight: 'min(520px, max(180px, calc(100vh - 340px)))' }}
      >
        {loading ? (
          <div className="p-20 text-center text-gray-500 dark:text-gray-400">
            <div className="animate-spin w-12 h-12 border-4 border-[#AAD08C] border-t-transparent rounded-full mx-auto mb-6 shadow-sm"></div>
            <p className="text-[10px] font-black uppercase tracking-widest opacity-60">{t('groups.cargando')}</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-20 text-center text-gray-500 dark:text-gray-400">
            <div className="text-6xl mb-6 opacity-20 transform hover:scale-110 transition-transform duration-500">🔔</div>
            <p className="text-[10px] font-black uppercase tracking-widest opacity-60">{t('notifications.sinnotificaciones')}</p>
          </div>
        ) : (
          <div className="p-4 flex flex-col gap-3">
            {notifications.slice(0, 10).map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClose={onClose}
              />
            ))}
          </div>
        )}
      </div>

      
      {/* Footer Botón Principal */}
      {notifications.length > 0 && (
        <div className="px-7 py-5 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950">
          <button
            onClick={handleViewAll}
            className="w-full h-12 flex items-center justify-center gap-3 bg-[#055590] hover:bg-[#055590]/90 text-white rounded-full font-black text-[10px] uppercase tracking-[0.25em] shadow-xl shadow-blue-900/10 transition-all active:scale-95"
          >
            {t('common.vertodaslasnotificaciones')}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M14 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
